/* Nursing pillow store behaviour: product-media gallery, colour + quantity picker, sticky bar, scroll reveal, strip pause,
   deal-card colour sync, per-set colours for 2 and 3 sets, fact count-up, lazy clips.
   Everything degrades to a working static page without JS. Motion: transform/opacity only, none for
   prefers-reduced-motion. */
(() => {
  if (window.__npInit) return;
  window.__npInit = true;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const io = (cb, opts) => ('IntersectionObserver' in window ? new IntersectionObserver(cb, opts) : null);
  const PHONE = 900;
  const COUNT_MS = 900;

  function initReveal() {
    const els = document.querySelectorAll('.np-reveal:not(.is-visible)');
    const obs = !reduced && io((entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-visible');
      obs.unobserve(e.target);
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    els.forEach((el) => (obs ? obs.observe(el) : el.classList.add('is-visible')));
  }

  const gallery = { showMedia: () => {} };

  function initGallery() {
    const g = document.querySelector('[data-np-gallery]');
    if (!g) return;
    const scroller = g.querySelector('[data-np-slides]');
    const slides = [...g.querySelectorAll('[data-np-slide]')];
    const thumbs = [...g.querySelectorAll('[data-np-thumb]')];
    const count = g.querySelector('[data-np-count]');
    let active = 0;

    const hydrate = (i) => {
      const img = slides[i] && slides[i].querySelector('img[data-src]');
      if (!img) return;
      img.src = (window.innerWidth < PHONE && img.dataset.srcM) || img.dataset.src;
      img.removeAttribute('data-src');
    };
    const mark = (i) => {
      active = i;
      slides.forEach((s, k) => s.setAttribute('aria-hidden', k === i ? 'false' : 'true'));
      thumbs.forEach((t, k) => t.setAttribute('aria-current', k === i ? 'true' : 'false'));
      if (count) count.textContent = String(i + 1);
      hydrate(i + 1);
    };
    const go = (i) => {
      const n = (i + slides.length) % slides.length;
      hydrate(n);
      scroller.scrollTo({ left: n * scroller.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
      mark(n);
    };
    thumbs.forEach((t) => {
      t.addEventListener('click', () => go(Number(t.dataset.npThumb)));
      t.addEventListener('pointerenter', () => hydrate(Number(t.dataset.npThumb)), { once: true });
    });
    g.querySelector('[data-np-prev]')?.addEventListener('click', () => go(active - 1));
    g.querySelector('[data-np-next]')?.addEventListener('click', () => go(active + 1));
    let t;
    scroller.addEventListener('scroll', () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const i = Math.round(scroller.scrollLeft / Math.max(1, scroller.clientWidth));
        if (i !== active) mark(i);
      }, 80);
    }, { passive: true });
    scroller.addEventListener('pointerdown', () => hydrate(1), { once: true });
    /* Colour swatches: jump to that variant's own photo. */
    gallery.showStudio = (src, srcM, alt, thumb) => {
      const main = g.querySelector('[data-np-main]');
      const mainThumb = g.querySelector('[data-np-main-thumb]');
      if (!main) return;
      const probe = new Image();
      main.classList.add('is-swapping');
      probe.onload = probe.onerror = () => {
        main.srcset = `${srcM} 800w, ${src} 1440w`;
        main.src = src;
        if (alt) main.alt = alt;
        requestAnimationFrame(() => main.classList.remove('is-swapping'));
      };
      probe.src = window.innerWidth < PHONE ? srcM : src;
      if (mainThumb) mainThumb.src = thumb || srcM;
      if (active !== 0) go(0);
    };
    gallery.showMedia = (id) => {
      const i = slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0 && i !== active) go(i);
    };
  }

  function initBuy() {
    const buy = document.querySelector('[data-np-buy]');
    if (!buy) return;
    const variantInput = buy.querySelector('[data-np-variant]');
    const qtyInput = buy.querySelector('[data-np-qty]');
    const now = buy.querySelector('[data-np-now]');
    const was = buy.querySelector('[data-np-was]');
    const save = buy.querySelector('[data-np-save]');
    const ctaText = buy.querySelector('[data-np-cta-text]');
    const ctaPrice = buy.querySelector('[data-np-cta-price]');
    const colourName = buy.querySelector('[data-np-colour-name]');
    const stickyLine = document.querySelector('[data-np-sticky-line]');
    const stickyImg = document.querySelector('[data-np-sticky-img]');
    const state = { colour: colourName ? colourName.textContent : '', price: now ? now.textContent : '', label: '' };

    const syncSticky = () => {
      if (stickyLine) stickyLine.textContent = `${state.colour ? state.colour + ' · ' : ''}${state.label ? state.label + ' · ' : ''}${state.price}`;
    };

    buy.querySelector('[data-np-colours]')?.addEventListener('change', (e) => {
      const r = e.target;
      if (r.type !== 'radio') return;
      variantInput.value = r.value;
      state.colour = r.dataset.name;
      if (colourName) colourName.textContent = r.dataset.name;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', r.value);
      history.replaceState(null, '', url);
      if (r.dataset.studio) gallery.showStudio(r.dataset.studio, r.dataset.studioM, `Nursing pillow set, ${r.dataset.name}`, r.dataset.thumb);
      else if (r.dataset.mediaId) gallery.showMedia(r.dataset.mediaId);
      if (stickyImg && r.dataset.thumb) stickyImg.src = r.dataset.thumb;
      document.querySelectorAll('[data-np-deal-colour]').forEach((sel) => {
        const opt = Array.from(sel.options).find((o) => o.value === r.value);
        if (opt && !opt.disabled) sel.value = r.value;
      });
      syncSticky();
    });

    /* 2 or 3 sets: "Set 2" / "Set 3" colour rows. A row follows the main colour until it is picked by hand. */
    const sets = buy.querySelector('[data-np-sets]');
    const mainColours = buy.querySelector('[data-np-colours]');
    const setRows = sets ? [...sets.querySelectorAll('[data-np-set-row]')] : [];
    const showSetRows = (qty) => {
      if (!sets) return;
      sets.hidden = qty < 2;
      /* With 2 or 3 sets the box shows Set 1..n; the single colour row hides so there is one place to pick. */
      if (mainColours) mainColours.hidden = qty > 1;
      setRows.forEach((row) => { row.hidden = Number(row.dataset.npSetRow) > qty; });
    };
    const markRow = (row, input) => {
      const name = row.querySelector('[data-np-set-name]');
      if (name) name.textContent = input.dataset.name;
    };
    setRows.forEach((row) => row.addEventListener('change', (e) => {
      if (e.target.type !== 'radio') return;
      markRow(row, e.target);
      if (e.target.hasAttribute('data-np-set1')) {
        /* Set 1 drives the real variant input, the gallery and the sticky bar through the main swatch. */
        const main = mainColours && mainColours.querySelector(`input[value="${e.target.value}"]`);
        if (main && !main.checked) main.click();
        return;
      }
      row.dataset.touched = '1';
    }));
    buy.querySelector('[data-np-colours]')?.addEventListener('change', (e) => {
      if (e.target.type !== 'radio') return;
      setRows.forEach((row) => {
        if (row.dataset.touched && row.dataset.npSetRow !== '1') return;
        const same = row.querySelector(`input[value="${e.target.value}"]`);
        if (same && !same.disabled) { same.checked = true; markRow(row, same); }
      });
    });

    /* Reads the checked option's data-* each time, so np-xmas.js can rewrite them when the sale ends. */
    const picker = buy.querySelector('[data-np-picker]');
    const applyPick = (r) => {
      qtyInput.value = r.value;
      state.price = r.dataset.now;
      state.label = r.value === '1' ? '' : r.dataset.label;
      now.textContent = r.dataset.now;
      was.textContent = r.dataset.was || '';
      was.hidden = !r.dataset.was;
      save.textContent = r.dataset.save || '';
      save.hidden = !r.dataset.save;
      if (ctaText) ctaText.textContent = r.dataset.cta;
      if (ctaPrice) ctaPrice.textContent = r.dataset.now;
      showSetRows(Number(r.value));
      syncSticky();
    };
    picker?.addEventListener('change', (e) => {
      if (e.target.type === 'radio') applyPick(e.target);
    });
    const resync = () => {
      const r = picker && picker.querySelector('input:checked');
      if (r) applyPick(r);
    };
    document.addEventListener('np:sale-ended', resync);
    if (document.documentElement.classList.contains('np-sale-over')) resync();
  }

  /* Mixed colours: a form whose sets differ adds one cart line per colour through /cart/add.js, then
     re-renders the drawer the way Dawn does. One colour for every set falls through to Dawn's own add. */
  const CART_SECTIONS = 'cart-drawer,cart-icon-bubble';
  const mixedItems = (form) => {
    const qty = Number(form.querySelector('[name="quantity"]')?.value) || 1;
    const first = form.querySelector('[name="id"]')?.value;
    if (qty < 2 || !first) return null;
    const extra = [...form.querySelectorAll('[data-np-set-pick]')]
      .filter((el) => el.tagName === 'SELECT' || (el.checked && !el.closest('[hidden]')))
      .map((el) => el.value)
      .slice(0, qty - 1);
    const ids = [first, ...extra];
    while (ids.length < qty) ids.push(first);
    if (ids.every((id) => id === first)) return null;
    const counts = ids.reduce((m, id) => m.set(id, (m.get(id) || 0) + 1), new Map());
    return [...counts].map(([id, quantity]) => ({ id: Number(id), quantity }));
  };

  async function addMixed(form, items) {
    const btn = form.querySelector('[type="submit"]');
    const spinner = btn?.querySelector('.loading__spinner');
    const errWrap = form.querySelector('.product-form__error-message-wrapper');
    const errMsg = form.querySelector('.product-form__error-message');
    btn?.setAttribute('aria-disabled', 'true');
    btn?.classList.add('loading');
    spinner?.classList.remove('hidden');
    if (errWrap) errWrap.hidden = true;
    try {
      const res = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: CART_SECTIONS, sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      const drawer = document.querySelector('cart-drawer');
      if (drawer && data.sections) {
        drawer.classList.remove('is-empty');
        drawer.renderContents({ sections: data.sections });
      } else {
        window.location.href = window.routes.cart_url;
      }
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'np-mixed-sets', cartData: data });
    } catch (err) {
      if (errMsg) errMsg.textContent = err.message;
      if (errWrap) errWrap.hidden = false;
    } finally {
      btn?.removeAttribute('aria-disabled');
      btn?.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  }

  function initMixedSets() {
    document.querySelectorAll('product-form').forEach((pf) => {
      const form = pf.querySelector('form');
      if (!form || !form.querySelector('[data-np-set-pick]')) return;
      /* Capture on the <product-form> runs before Dawn's submit listener on the <form>. */
      pf.addEventListener('submit', (e) => {
        const items = mixedItems(form);
        if (!items) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        addMixed(form, items);
      }, true);
    });
  }

  function initSticky() {
    const bar = document.querySelector('[data-np-sticky]');
    const main = document.querySelector('[data-np-main-add]');
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    const footer = document.querySelector('.np-footer');
    let mainVisible = true;
    let footerVisible = false;
    const update = () => {
      const on = !mainVisible && !footerVisible && main.getBoundingClientRect().top < 0;
      bar.classList.toggle('is-on', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      bar.querySelector('button').tabIndex = on ? 0 : -1;
    };
    io(([e]) => { mainVisible = e.isIntersecting; update(); }).observe(main);
    if (footer) io(([e]) => { footerVisible = e.isIntersecting; update(); }).observe(footer);
    bar.querySelector('[data-np-sticky-add]').addEventListener('click', () => {
      const form = main.closest('form');
      if (form.requestSubmit) form.requestSubmit(main); else main.click();
    });
  }

  function initStrip() {
    document.querySelectorAll('.np-strip').forEach((s) => {
      const obs = io(([e]) => s.classList.toggle('is-off', !e.isIntersecting));
      if (obs) obs.observe(s);
    });
  }

  /* Feeding facts count up once when they come into view. The final number is in the HTML already. */
  function initCountUp() {
    const nums = [...document.querySelectorAll('[data-np-count-to]')];
    if (!nums.length || reduced) return;
    const run = (el) => {
      const to = Number(el.dataset.npCountTo) || 0;
      const t0 = performance.now();
      const step = (t) => {
        const k = Math.min(1, (t - t0) / COUNT_MS);
        const eased = 1 - Math.pow(1 - k, 3);
        el.textContent = String(Math.round(to * eased));
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const obs = io((entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      obs.unobserve(e.target);
      run(e.target);
    }), { threshold: 0.6 });
    if (!obs) return;
    nums.forEach((el) => obs.observe(el));
  }

  /* Clips: src only after window load and only while in view; paused off-screen.
     Nothing loads for reduced motion or Save-Data: the poster image stays. */
  function initVideos() {
    const vids = [...document.querySelectorAll('video[data-np-video]')];
    if (!vids.length || reduced || !('IntersectionObserver' in window)) return;
    if (navigator.connection && navigator.connection.saveData) return;
    const arm = () => {
      const obs = new IntersectionObserver((entries) => entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting) {
          if (v.dataset.src) {
            v.addEventListener('playing', () => v.classList.add('is-playing'), { once: true });
            v.src = v.dataset.src;
            v.removeAttribute('data-src');
          }
          const p = v.play();
          if (p && p.catch) p.catch(() => { /* autoplay refused: the poster stays */ });
        } else if (!v.paused) {
          v.pause();
        }
      }), { rootMargin: '120px 0px', threshold: 0.2 });
      vids.forEach((v) => obs.observe(v));
    };
    if (document.readyState === 'complete') arm();
    else window.addEventListener('load', arm, { once: true });
  }

  const start = () => { initReveal(); initGallery(); initBuy(); initMixedSets(); initSticky(); initStrip(); initCountUp(); initVideos(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
